import { GraphQLClient, gql, type ClientError } from 'graphql-request';
import { Query, QueryScoreAssessmentsForFirmArgs } from '../generated/graphql';
import { MonetaryAmountString } from './fragments';

export interface GetScoreAssessmentsOnDemandForFirmArgs extends QueryScoreAssessmentsForFirmArgs {
  includeEstateStatus?: boolean;
}

const ESTATE_STATUS_MISSING_FIELD_MESSAGE = 'Cannot query field "es" on type "ScorecardScores".';

const isEstateStatusMissingFieldValidationError = (error: unknown): boolean => {
  const errors = (error as ClientError | undefined)?.response?.errors;

  return (
    Array.isArray(errors) &&
    errors.length === 1 &&
    errors[0]?.extensions?.code === 'GRAPHQL_VALIDATION_FAILED' &&
    errors[0].message.startsWith(ESTATE_STATUS_MISSING_FIELD_MESSAGE)
  );
};

export const getScoreAssessmentsQuery = (includeEstateStatus = false) => gql`
  query ScoreAssessmentsForFirm($firmID: ID!, $cursor: String) {
    scoreAssessmentsForFirm(firmID: $firmID, cursor: $cursor) {
      items {
        firmClient {
          id
          mode
          firmID
          givenNamesDisplay
          familyNamesDisplay
          household {
            id
          }
          aiSummary
          individuals {
            age
            emailAddress
            familyName
            givenName
            profileColor
            profileImage
          }
          group {
            id
            name
          }
        }
        netWorth {${MonetaryAmountString}}
        annualIncome {${MonetaryAmountString}}
        annualDebtPayments {${MonetaryAmountString}}
        annualSavings {${MonetaryAmountString}}
        liquidity {${MonetaryAmountString}}
        taxInfo {
          mostRecentTaxYear
          taxesPaid {${MonetaryAmountString}}
          taxedIncome {${MonetaryAmountString}}
        }
        annualExpenses {${MonetaryAmountString}}
        qualifiedAssets {${MonetaryAmountString}}
        realEstateEquity {${MonetaryAmountString}}
        businessEquity {${MonetaryAmountString}}
        scorecard {
          sr
          br
          dr
          tr
          tt
          lt
          qt
          rt
          bt
          ir
          ${includeEstateStatus ? 'es' : ''}
        }
        lifeCoverages {
          person {
            givenName
          }
          overallCoverage
        }
        disabilityCoverages {
          person {
            givenName
          }
          overallCoverage
        }
        liabilityCoverages {
          person {
            givenName
          }
          overallCoverage
        }
      }
    }
  }
`;

export const clientGetScoreAssessmentsOnDemandForFirm = async (
  client: GraphQLClient,
  args: GetScoreAssessmentsOnDemandForFirmArgs
) => {
  const { includeEstateStatus, ...queryArgs } = args;

  try {
    return (
      await client.request<Pick<Query, 'scoreAssessmentsForFirm'>>(
        getScoreAssessmentsQuery(includeEstateStatus),
        queryArgs
      )
    ).scoreAssessmentsForFirm;
  } catch (error) {
    if (!includeEstateStatus || !isEstateStatusMissingFieldValidationError(error)) {
      throw error;
    }

    return (await client.request<Pick<Query, 'scoreAssessmentsForFirm'>>(getScoreAssessmentsQuery(false), queryArgs))
      .scoreAssessmentsForFirm;
  }
};
